/**
 * PropertySheetModal — 맵에 렌더링된 객체 전체를 엑셀 시트처럼 보여주고
 * 셀 단위로 속성 값을 바로 수정하는 **우측 절반 분할 패널**.
 *
 * 에디터 <main> flex 레이아웃의 형제 요소로 들어가 캔버스를 왼쪽 절반으로
 * **밀어내며** 열린다(오버레이가 아님). 왼쪽 절반의 맵을 보면서 수정하면
 * 즉시 반영되는 것을 확인할 수 있다. Excel 메뉴의 "객체 수정" 과
 * 자동 관계 생성(AutoLinkModal 적용) 직후 결과 검수용으로 열린다.
 * 행 추가/삭제는 지원하지 않는다 — 현재 맵에 렌더링된 객체가 행의 전부이고,
 * 편집은 그 객체들의 `data.properties` 값 수정(추가)만 허용한다.
 *
 * 컬럼 구성:
 *  · 이름/타입 고정 컬럼 + 표시 중인 타입들의 template 속성(union, name 제외)
 *  · 관계 컬럼 — relationTypes 중 sourceType 이 표시 타입인 것. 연결 대상의
 *    "이름"을 쉼표로 나열해 표시/편집한다. 편집 커밋 시 이름→객체 id 로
 *    해석하며(동명이거나 못 찾으면 거부), cardinality 1 이면 단일 id,
 *    아니면 id 배열로 저장한다(updateRelationship 과 동일한 저장 규칙).
 */
import React, { useMemo, useState } from 'react'
import { dia } from '@joint/core'
import type { TemplateRelationType } from '../../../map-editor-entities/schema/templateSchema'
import type { ObjectType } from '../../../map-editor-shared/store/objectTypeStore'
import { isRelationshipProperty } from '../../../map-editor-features/editor/lib/export/relationshipExtractor'
import { parseCardinality } from '../../../map-editor-features/editor/lib/relationshipUtils'
import styles from './PropertySheetModal.module.css'

interface PropertySheetModalProps {
  isOpen: boolean
  onClose: () => void
  graph: dia.Graph | null
  objectTypes: ObjectType[]
  relationTypes: Record<string, TemplateRelationType>
  /** data.typeId 가 없는(CSV 매핑 기반) 요소의 타입 해석 — ExcelMenu 와 동일 규칙. */
  resolveTypeId?: (element: dia.Element) => string | undefined
  onObjectUpdate: (id: string, updates: Partial<any>) => void
  /** 행의 # 클릭 → 맵에서 그 객체로 이동·선택. */
  onFocusObject?: (id: string) => void
  /** 에디터 저장(서버 반영) — 셀 수정은 맵에 즉시 적용되지만 서버 저장은 이 버튼으로.
   *  실패 시 reject 하면 시트가 에러를 표시하고 변경 목록을 유지한다. */
  onSave?: () => Promise<void> | void
  isSaving?: boolean
}

/** 시트에서 수정한 셀 하나의 변경 내역(저장 요약용). */
interface SheetChange {
  rowName: string
  column: string
  from: string
  to: string
}

interface SheetRow {
  id: string
  el: dia.Element
  typeId: string
  typeName: string
  props: Record<string, any>
}

interface RelColumn {
  relationKey: string
  propertyKey: string
  label: string
  cardinality: string
  sourceType: string
}

interface EditingCell {
  rowId: string
  colKey: string
  draft: string
  error: string | null
}

export const PropertySheetModal: React.FC<PropertySheetModalProps> = ({
  isOpen,
  onClose,
  graph,
  objectTypes,
  relationTypes,
  resolveTypeId,
  onObjectUpdate,
  onFocusObject,
  onSave,
  isSaving,
}) => {
  // 셀 커밋 후 graph 를 다시 읽어 행을 재계산하기 위한 카운터.
  const [revision, setRevision] = useState(0)
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [editing, setEditing] = useState<EditingCell | null>(null)
  const [focusedRowId, setFocusedRowId] = useState<string | null>(null)

  // 저장 전 변경 내역 — 없으면 저장 비활성화, 저장하면 요약으로 표시하고 비운다.
  const [pendingChanges, setPendingChanges] = useState<SheetChange[]>([])
  const [savedChanges, setSavedChanges] = useState<SheetChange[] | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const allRows = useMemo<SheetRow[]>(() => {
    if (!isOpen || !graph) return []
    const rows: SheetRow[] = []
    graph.getElements().forEach(el => {
      const data = el.get('data')
      if (!data) return
      const typeId: string | undefined = data.typeId ?? resolveTypeId?.(el)
      if (!typeId) return
      const type = objectTypes.find(t => t.id === typeId)
      rows.push({
        id: el.id as string,
        el,
        typeId,
        // 항상 문자열로 — 매칭 타입이 없으면 typeId(숫자일 수 있음)가 들어와
        // 아래 정렬의 localeCompare 가 터지지 않게 한다.
        typeName: String(type?.name ?? typeId),
        props: data.properties ?? {},
      })
    })
    rows.sort(
      (a, b) =>
        a.typeName.localeCompare(b.typeName) ||
        String(a.props.name ?? '').localeCompare(String(b.props.name ?? ''), undefined, {
          numeric: true,
        })
    )
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, graph, objectTypes, resolveTypeId, revision])

  // 필터 옵션 — 맵에 실제 존재하는 타입만.
  const presentTypes = useMemo(() => {
    const ids = new Set(allRows.map(r => r.typeId))
    return objectTypes.filter(t => ids.has(t.id))
  }, [allRows, objectTypes])

  const rows = useMemo(
    () => (typeFilter === 'all' ? allRows : allRows.filter(r => r.typeId === typeFilter)),
    [allRows, typeFilter]
  )

  // 표시 중인 타입들의 template 속성 union (name 은 고정 컬럼이므로 제외).
  const propColumns = useMemo(() => {
    const includedTypeIds = new Set(rows.map(r => r.typeId))
    const cols: { key: string }[] = []
    const seen = new Set<string>()
    presentTypes
      .filter(t => includedTypeIds.has(t.id))
      .forEach(t => {
        ;(Array.isArray(t.properties) ? t.properties : []).forEach(p => {
          // 관계 속성(…Id/…Ids)은 🔗 관계 컬럼으로만 편집한다 — 일반 셀로 열면
          // 문자열이 그대로 저장돼 export 스키마(배열)와 어긋난다.
          if (p.key === 'name' || seen.has(p.key) || isRelationshipProperty(p.key)) return
          seen.add(p.key)
          cols.push({ key: p.key })
        })
      })
    return cols
  }, [rows, presentTypes])

  // 관계 컬럼 — sourceType 이 표시 타입인 관계.
  const relColumns = useMemo<RelColumn[]>(() => {
    const includedTypeIds = new Set(rows.map(r => r.typeId))
    return Object.entries(relationTypes)
      .filter(([, rel]) => includedTypeIds.has(rel.sourceType))
      .map(([key, rel]) => ({
        relationKey: key,
        propertyKey: rel.propertyKey,
        label: rel.name || rel.propertyKey,
        cardinality: rel.cardinality,
        sourceType: rel.sourceType,
      }))
  }, [rows, relationTypes])

  // 관계 편집용 이름→객체 id 인덱스 (전체 그래프 대상 — 타깃은 다른 타입).
  const nameIndex = useMemo(() => {
    const m = new Map<string, string[]>()
    if (!isOpen || !graph) return m
    graph.getElements().forEach(el => {
      const nm = el.get('data')?.properties?.name
      if (nm == null || nm === '') return
      const key = String(nm).trim()
      m.set(key, [...(m.get(key) ?? []), el.id as string])
    })
    return m
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, graph, revision])

  const idToName = (id: string): string => {
    const cell = graph?.getCell(id)
    const nm = cell?.get('data')?.properties?.name
    return nm ? String(nm) : id
  }

  const relDisplayValue = (row: SheetRow, col: RelColumn): string => {
    const v = row.props[col.propertyKey]
    if (v == null) return ''
    const ids = Array.isArray(v) ? v : [v]
    return ids.map(id => idToName(String(id))).join(', ')
  }

  const propDisplayValue = (row: SheetRow, key: string): string => {
    const v = row.props[key]
    if (v == null) return ''
    return Array.isArray(v) ? v.join(', ') : String(v)
  }

  const commitData = (row: SheetRow, propertyKey: string, value: unknown) => {
    // PropertyEditor 와 동일하게 live data.properties 위에 merge — 다른 키
    // (다른 관계·사용자 속성)를 지우지 않는다.
    const currentData = row.el.get('data') || {}
    const newData = {
      ...currentData,
      properties: { ...(currentData.properties || {}), [propertyKey]: value },
    }
    onObjectUpdate(row.id, { data: newData })
    setRevision(r => r + 1)
  }

  // 변경 내역 기록 — 같은 셀 재수정은 하나로 합치고(최초 from 유지),
  // 원래 값으로 되돌리면 목록에서 제거한다.
  const recordChange = (row: SheetRow, column: string, from: string, to: string) => {
    if (from === to) return
    const rowName = String(row.props.name ?? row.id)
    setSavedChanges(null)
    setSaveError(null)
    setPendingChanges(prev => {
      const idx = prev.findIndex(c => c.rowName === rowName && c.column === column)
      if (idx >= 0) {
        if (prev[idx].from === to) return prev.filter((_, i) => i !== idx)
        const merged = [...prev]
        merged[idx] = { ...merged[idx], to }
        return merged
      }
      return [...prev, { rowName, column, from, to }]
    })
  }

  const dirty = pendingChanges.length > 0

  const handleSaveClick = async () => {
    if (!onSave || !dirty) return
    setSaveError(null)
    try {
      await onSave()
      setSavedChanges(pendingChanges)
      setPendingChanges([])
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : '저장에 실패했습니다.')
    }
  }

  const startEdit = (row: SheetRow, colKey: string, current: string) => {
    setEditing({ rowId: row.id, colKey, draft: current, error: null })
  }

  const cancelEdit = () => setEditing(null)

  const commitEdit = (row: SheetRow) => {
    if (!editing) return
    const relCol = relColumns.find(c => `rel:${c.relationKey}` === editing.colKey)

    if (!relCol) {
      // 일반 속성(이름 포함): 입력 문자열 그대로 저장.
      const key = editing.colKey === 'name' ? 'name' : editing.colKey
      const before = propDisplayValue(row, key)
      commitData(row, key, editing.draft)
      recordChange(row, key === 'name' ? '이름' : key, before, editing.draft)
      setEditing(null)
      return
    }

    // 관계 컬럼: 쉼표 구분 이름(또는 객체 id)을 id 목록으로 해석.
    const tokens = editing.draft
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)
    const ids: string[] = []
    const bad: string[] = []
    tokens.forEach(t => {
      const matches = nameIndex.get(t)
      if (matches && matches.length === 1) ids.push(matches[0])
      else if (matches && matches.length > 1) bad.push(`${t}(동명 ${matches.length}개)`)
      else if (graph?.getCell(t)) ids.push(t)
      else bad.push(t)
    })
    if (bad.length > 0) {
      setEditing({ ...editing, error: `찾을 수 없음: ${bad.join(', ')}` })
      return
    }
    const unique = Array.from(new Set(ids))
    const max = parseCardinality(relCol.cardinality)
    if (max !== null && unique.length > max) {
      setEditing({ ...editing, error: `최대 ${max}개까지 연결할 수 있습니다.` })
      return
    }
    const value =
      unique.length === 0 ? undefined : max === 1 ? unique[0] : unique
    const before = relDisplayValue(row, relCol)
    commitData(row, relCol.propertyKey, value)
    recordChange(row, relCol.label, before, unique.map(idToName).join(', '))
    setEditing(null)
  }

  if (!isOpen) return null

  const renderCell = (row: SheetRow, colKey: string, display: string, editable: boolean) => {
    const isEditing = editing?.rowId === row.id && editing?.colKey === colKey
    if (isEditing && editing) {
      return (
        <input
          className={`${styles.cellInput} ${editing.error ? styles.cellInputError : ''}`}
          value={editing.draft}
          autoFocus
          title={editing.error ?? undefined}
          onChange={e => setEditing({ ...editing, draft: e.target.value, error: null })}
          onBlur={() => commitEdit(row)}
          onKeyDown={e => {
            if (e.key === 'Enter') commitEdit(row)
            if (e.key === 'Escape') cancelEdit()
          }}
        />
      )
    }
    return (
      <div
        className={editable ? styles.cellValue : styles.cellReadonly}
        onClick={editable ? () => startEdit(row, colKey, display) : undefined}
        title={display || undefined}
      >
        {display}
      </div>
    )
  }

  return (
    <div className={styles.panel} role="dialog" aria-label="객체 속성 시트">
        <div className={styles.header}>
          <span className={styles.title}>📋 객체 속성 시트</span>
          <select
            className={styles.typeFilter}
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
            aria-label="타입 필터"
          >
            <option value="all">전체 타입 ({allRows.length})</option>
            {presentTypes.map(t => (
              <option key={t.id} value={t.id}>
                {t.name} ({allRows.filter(r => r.typeId === t.id).length})
              </option>
            ))}
          </select>
          <span className={styles.count}>{rows.length}개 객체</span>
          {onSave && (
            <button
              type="button"
              className={styles.saveButton}
              onClick={() => void handleSaveClick()}
              disabled={!dirty || isSaving}
              title={dirty ? '수정 내용을 서버에 저장' : '변경사항 없음'}
            >
              {isSaving ? '저장 중...' : dirty ? `💾 저장 (${pendingChanges.length})` : '💾 저장'}
            </button>
          )}
          <button type="button" className={styles.closeButton} onClick={onClose}>
            닫기
          </button>
        </div>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.indexCol}>#</th>
                <th>이름</th>
                <th>타입</th>
                {propColumns.map(c => (
                  <th key={c.key}>{c.key}</th>
                ))}
                {relColumns.map(c => (
                  <th key={c.relationKey} className={styles.relHeader}>
                    🔗 {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id} className={focusedRowId === row.id ? styles.rowFocused : undefined}>
                  <td className={styles.indexCol}>
                    <button
                      type="button"
                      className={styles.focusButton}
                      title="맵에서 보기"
                      onClick={() => {
                        setFocusedRowId(row.id)
                        onFocusObject?.(row.id)
                      }}
                    >
                      {i + 1}
                    </button>
                  </td>
                  <td>{renderCell(row, 'name', propDisplayValue(row, 'name'), true)}</td>
                  <td>
                    {/* 타입 변경 — EditorPage handleObjectUpdate 가 objectTypeId
                        prop·스타일·사이드바 그룹(layer)까지 함께 옮긴다. */}
                    <select
                      className={styles.typeFilter}
                      value={row.typeId}
                      onChange={e => {
                        const newTypeId = e.target.value
                        if (!newTypeId || newTypeId === row.typeId) return
                        const newType = objectTypes.find(t => t.id === newTypeId)
                        recordChange(row, '타입', row.typeName, String(newType?.name ?? newTypeId))
                        const currentData = row.el.get('data') || {}
                        onObjectUpdate(row.id, { data: { ...currentData, typeId: newTypeId } })
                        setRevision(r => r + 1)
                      }}
                    >
                      {!objectTypes.some(t => t.id === row.typeId) && (
                        <option value={row.typeId}>{row.typeName}</option>
                      )}
                      {objectTypes.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  {propColumns.map(c => (
                    <td key={c.key}>
                      {renderCell(row, c.key, propDisplayValue(row, c.key), true)}
                    </td>
                  ))}
                  {relColumns.map(c => {
                    // 이 행의 타입이 관계의 source 가 아니면 편집 불가(빈 셀).
                    const applies = c.sourceType === row.typeId
                    return (
                      <td key={c.relationKey}>
                        {applies
                          ? renderCell(row, `rel:${c.relationKey}`, relDisplayValue(row, c), true)
                          : <div className={styles.cellDisabled} />}
                      </td>
                    )
                  })}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={3 + propColumns.length + relColumns.length}>
                    <div className={styles.empty}>표시할 객체가 없습니다.</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.footer}>
          {editing?.error ? (
            <span className={styles.errorText}>{editing.error}</span>
          ) : saveError ? (
            <span className={styles.errorText}>{saveError}</span>
          ) : savedChanges ? (
            <div className={styles.savedSummary}>
              <span className={styles.savedTitle}>
                ✅ 저장됐습니다 — 변경 {savedChanges.length}건
              </span>
              <div className={styles.changeList}>
                {savedChanges.map((c, i) => (
                  <div key={i} className={styles.changeLine}>
                    {c.rowName} · {c.column}: {c.from || '(비어 있음)'} → {c.to || '(비어 있음)'}
                  </div>
                ))}
              </div>
            </div>
          ) : dirty ? (
            <span className={styles.dirtyHint}>
              저장되지 않은 변경 {pendingChanges.length}건 — 위 💾 저장 버튼으로 서버에
              반영하세요.
            </span>
          ) : (
            <span className={styles.hint}>
              셀을 클릭해 수정하면 맵 객체에 즉시 반영됩니다. 관계 셀은 대상 이름을
              쉼표로 구분해 입력하세요. 행 추가/삭제는 지원하지 않습니다.
            </span>
          )}
        </div>
    </div>
  )
}
